import { Button, Form } from 'react-bootstrap'
import { MdAdd, MdArrowDownward, MdArrowUpward, MdDelete } from 'react-icons/md'

import {
    cloneTargetsWithNewIds,
    getGoalTargetKey,
    getTargetPhaseDateRange,
    getTargetPhaseRange,
    makeFixedTargetPhase,
    makeGoalTarget,
    setGoalTargetChoice,
} from './goalPhases'

const getLatestStartedPeriodIndex = (goal) => {
    const indexes = [
        goal?.progress?.current?.periodIndex,
        ...(goal?.progress?.history || []).map((period) => period.periodIndex),
    ].map(Number).filter(Number.isInteger)
    return indexes.length > 0 ? Math.max(...indexes) : -1
}

const formatPhaseDateRange = (dateRange) => {
    if (!dateRange.reached) return 'Not reached before the campaign ends'
    const inclusiveEnd = dateRange.end.clone().subtract(1, 'day')
    return `${dateRange.start.format('D MMM')} – ${inclusiveEnd.format('D MMM YYYY')}`
}

function GoalTargetPhasesEditor({ goal, targetChoices, timezone, campaignStarted, onUpdate }) {
    const phases = goal.targetPhases || []
    const latestStartedPeriodIndex = getLatestStartedPeriodIndex(goal)
    const currentPeriodIndex = Number(goal?.progress?.current?.periodIndex)

    const updateTargetChoice = (phaseId, targetId, encoded) => onUpdate((draft) => {
        const phase = draft.targetPhases.find((candidate) => candidate.id === phaseId)
        const target = phase.targets.find((candidate) => candidate.id === targetId)
        setGoalTargetChoice(target, encoded)
    })

    const addPhase = () => onUpdate((draft) => {
        const remainderIndex = draft.targetPhases.findIndex((phase) => phase.kind === 'remainder')
        const remainder = draft.targetPhases[remainderIndex]
        const copiedTargets = cloneTargetsWithNewIds(remainder?.targets || [makeGoalTarget()])
        draft.targetPhases.splice(remainderIndex, 0, makeFixedTargetPhase(copiedTargets))
    })

    const movePhase = (phaseIndex, direction) => onUpdate((draft) => {
        const destination = phaseIndex + direction
        const lastFixedIndex = draft.targetPhases.length - 2
        if (destination < 0 || destination > lastFixedIndex) return
        const [phase] = draft.targetPhases.splice(phaseIndex, 1)
        draft.targetPhases.splice(destination, 0, phase)
    })

    return (
        <section className="goal-editor-section">
            <h3>Target Phases</h3>
            <p>Set targets for consecutive evaluation periods. The Rest phase always covers every evaluation after the fixed phases.</p>
            {campaignStarted && (
                <div className="goal-phase-lock-note">
                    Phase targets and structure can be edited. Changes that move the active evaluation into another phase apply immediately; completed evaluations keep their saved results unless recalculation is selected below.
                </div>
            )}

            <div className="goal-phase-list">
                {phases.map((phase, phaseIndex) => {
                    const range = getTargetPhaseRange(phases, phaseIndex)
                    const dateRange = getTargetPhaseDateRange(goal, phaseIndex, timezone)
                    const isRemainder = phase.kind === 'remainder'
                    const isActive = Number.isInteger(currentPeriodIndex)
                        && currentPeriodIndex >= range.evaluationStart - 1
                        && (range.evaluationEnd === null || currentPeriodIndex <= range.evaluationEnd - 1)
                    const isCompleted = !isActive && latestStartedPeriodIndex >= range.evaluationStart - 1
                    const phaseTitle = isRemainder ? 'Rest' : `Phase ${phaseIndex + 1}`
                    const rangeLabel = range.evaluationEnd === null
                        ? `Evaluation ${range.evaluationStart} onward`
                        : `Evaluations ${range.evaluationStart}–${range.evaluationEnd}`

                    return (
                        <div className={`goal-phase-card${isRemainder ? ' goal-phase-card--remainder' : ''}`} key={phase.id}>
                            <div className="goal-phase-heading">
                                <div>
                                    <div className="goal-phase-title-row">
                                        <strong>{phaseTitle}</strong>
                                        {isRemainder && <span className="goal-phase-required">Required</span>}
                                        {isActive && <span className="goal-phase-status goal-phase-status--active">Active</span>}
                                        {isCompleted && <span className="goal-phase-status">Completed</span>}
                                    </div>
                                    <span>{rangeLabel} · {formatPhaseDateRange(dateRange)}</span>
                                </div>
                                {!isRemainder && (
                                    <div className="goal-phase-actions">
                                        <Button
                                            variant="outline-light"
                                            className="goal-icon-button"
                                            disabled={phaseIndex === 0}
                                            onClick={() => movePhase(phaseIndex, -1)}
                                            aria-label={`Move ${phaseTitle} up`}
                                        ><MdArrowUpward /></Button>
                                        <Button
                                            variant="outline-light"
                                            className="goal-icon-button"
                                            disabled={phaseIndex === phases.length - 2}
                                            onClick={() => movePhase(phaseIndex, 1)}
                                            aria-label={`Move ${phaseTitle} down`}
                                        ><MdArrowDownward /></Button>
                                        <Button
                                            variant="outline-danger"
                                            className="goal-icon-button"
                                            onClick={() => onUpdate((draft) => {
                                                draft.targetPhases = draft.targetPhases.filter((candidate) => candidate.id !== phase.id)
                                            })}
                                            aria-label={`Delete ${phaseTitle}`}
                                        ><MdDelete /></Button>
                                    </div>
                                )}
                            </div>

                            {isRemainder ? (
                                <div className="goal-phase-duration goal-phase-duration--rest">Applies for the rest of the campaign</div>
                            ) : (
                                <Form.Group className="goal-phase-duration">
                                    <Form.Label>Number of evaluation periods</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={phase.evaluationCount}
                                        onChange={(event) => {
                                            const { value } = event.target
                                            onUpdate((draft) => {
                                                draft.targetPhases.find((candidate) => candidate.id === phase.id).evaluationCount = value
                                            })
                                        }}
                                        aria-label={`${phaseTitle} evaluation periods`}
                                    />
                                </Form.Group>
                            )}

                            <div className="goal-phase-targets">
                                {phase.targets.map((target) => (
                                    <div className="goal-target-editor" key={target.id}>
                                        <Form.Select
                                            value={getGoalTargetKey(target)}
                                            onChange={(event) => updateTargetChoice(phase.id, target.id, event.target.value)}
                                            aria-label={`${phaseTitle} target metric`}
                                        >
                                            {targetChoices.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}
                                        </Form.Select>
                                        <Form.Control
                                            type="number"
                                            min="0.0001"
                                            step={target.metric === 'attempts' || target.metric === 'answerCount' ? '1' : 'any'}
                                            value={target.threshold}
                                            onChange={(event) => {
                                                const { value } = event.target
                                                onUpdate((draft) => {
                                                    const draftPhase = draft.targetPhases.find((candidate) => candidate.id === phase.id)
                                                    draftPhase.targets.find((candidate) => candidate.id === target.id).threshold = value
                                                })
                                            }}
                                            aria-label={`${phaseTitle} target threshold`}
                                        />
                                        <Button
                                            variant="outline-danger"
                                            className="goal-icon-button"
                                            disabled={phase.targets.length === 1}
                                            onClick={() => onUpdate((draft) => {
                                                const draftPhase = draft.targetPhases.find((candidate) => candidate.id === phase.id)
                                                draftPhase.targets = draftPhase.targets.filter((candidate) => candidate.id !== target.id)
                                            })}
                                            aria-label={`Delete target from ${phaseTitle}`}
                                        ><MdDelete /></Button>
                                    </div>
                                ))}
                            </div>
                            <Button
                                variant="outline-light"
                                onClick={() => onUpdate((draft) => {
                                    draft.targetPhases.find((candidate) => candidate.id === phase.id).targets.push(makeGoalTarget())
                                })}
                            ><MdAdd /> Add Target</Button>
                        </div>
                    )
                })}
            </div>

            <Button variant="outline-light" onClick={addPhase}>
                <MdAdd /> Add Phase
            </Button>

            {campaignStarted && (goal?.progress?.history || []).length > 0 && (
                <div className="goal-history-recalculation">
                    <Form.Check
                        type="checkbox"
                        id={`goal-recalculate-history-${goal._id || 'new'}`}
                        checked={Boolean(goal.recalculateHistory)}
                        onChange={(event) => {
                            const { checked } = event.target
                            onUpdate((draft) => { draft.recalculateHistory = checked })
                        }}
                        label="Recalculate completed evaluations using the revised phase schedule and targets"
                    />
                    <small>
                        This can change historical Achieved/Missed results. Question-based targets require that those questions were collected in every affected historical session.
                    </small>
                </div>
            )}
        </section>
    )
}

export default GoalTargetPhasesEditor
